import React from 'react';

/**
 * Format content text to display properly with:
 * - Numbered lists (1., 2., 3.)
 * - Bullet points (-, •)
 * - Headings (# Heading)
 * - Bold text (**text**)
 * - Line breaks
 */
export const formatContent = (content: string): React.ReactNode => {
  if (!content) return null;

  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let inList = false;
  let listItems: React.ReactNode[] = [];
  let listType: 'ordered' | 'unordered' | null = null;

  const flushList = () => {
    if (listItems.length > 0) {
      if (listType === 'ordered') {
        elements.push(
          <ol key={`list-${elements.length}`} className="list-decimal list-outside space-y-3 mb-5 ml-6 pl-2 text-gray-800">
            {listItems}
          </ol>
        );
      } else if (listType === 'unordered') {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc list-outside space-y-3 mb-5 ml-6 pl-2 text-gray-800">
            {listItems}
          </ul>
        );
      }
      listItems = [];
      listType = null;
      inList = false;
    }
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    // Empty line
    if (!trimmed) {
      flushList();
      elements.push(<br key={`br-${index}`} />);
      return;
    }

    // Heading (# Heading)
    if (trimmed.startsWith('#')) {
      flushList();
      const level = trimmed.match(/^#+/)?.[0].length || 1;
      const text = trimmed.replace(/^#+\s*/, '');
      const HeadingTag = `h${Math.min(level, 6)}` as keyof JSX.IntrinsicElements;
      const className = 
        level === 1 ? 'text-2xl font-bold mb-4 mt-6 text-gray-900' :
        level === 2 ? 'text-xl font-bold mb-3 mt-5 text-gray-900' :
        level === 3 ? 'text-lg font-semibold mb-2 mt-4 text-gray-900' :
        'text-base font-semibold mb-2 mt-3 text-gray-900';
      elements.push(
        <HeadingTag key={`heading-${index}`} className={className}>
          {formatInlineText(text)}
        </HeadingTag>
      );
      return;
    }

    // Ordered list (1., 2., 3. or 1), 2), etc.)
    const orderedMatch = trimmed.match(/^(\d+)[.)]\s*(.+)$/);
    if (orderedMatch) {
      if (!inList || listType !== 'ordered') {
        flushList();
        inList = true;
        listType = 'ordered';
      }
      listItems.push(
        <li key={`item-${listItems.length}`} className="mb-2 leading-relaxed">
          {formatInlineText(orderedMatch[2])}
        </li>
      );
      return;
    }

    // Unordered list (-, •, *)
    const unorderedMatch = trimmed.match(/^[-•*]\s+(.+)$/);
    if (unorderedMatch) {
      if (!inList || listType !== 'unordered') {
        flushList();
        inList = true;
        listType = 'unordered';
      }
      listItems.push(
        <li key={`item-${listItems.length}`} className="mb-2 leading-relaxed">
          {formatInlineText(unorderedMatch[1])}
        </li>
      );
      return;
    }

    // Sub-list item (indented with spaces or tabs) - create nested list
    const subListMatch = trimmed.match(/^(\s+)[-•*]\s+(.+)$/);
    if (subListMatch && inList) {
      // For now, just add as regular list item with indentation
      listItems.push(
        <li key={`subitem-${listItems.length}`} className="mb-2">
          <ul className="list-disc list-outside ml-6 mt-1 space-y-1">
            <li className="leading-relaxed">{formatInlineText(subListMatch[2])}</li>
          </ul>
        </li>
      );
      return;
    }

    // Sub-ordered list (indented numbers)
    const subOrderedMatch = trimmed.match(/^(\s+)(\d+)[.)]\s+(.+)$/);
    if (subOrderedMatch && inList && listType === 'ordered') {
      listItems.push(
        <li key={`subitem-${listItems.length}`} className="mb-2">
          <ul className="list-disc list-outside ml-6 mt-1 space-y-1">
            <li className="leading-relaxed">{formatInlineText(subOrderedMatch[3])}</li>
          </ul>
        </li>
      );
      return;
    }

    // Regular paragraph
    flushList();
    // Check if it's a paragraph with special formatting
    if (trimmed.includes('**') || (trimmed.includes('*') && !trimmed.startsWith('*'))) {
      elements.push(
        <p key={`p-${index}`} className="mb-4 leading-relaxed text-gray-800 text-base">
          {formatInlineText(trimmed)}
        </p>
      );
    } else {
      elements.push(
        <p key={`p-${index}`} className="mb-4 leading-relaxed text-gray-800 text-base">
          {trimmed}
        </p>
      );
    }
  });

  flushList();

  return <div className="space-y-2">{elements}</div>;
};

/**
 * Format inline text with bold (**text**), italic (*text*), and emphasis
 */
const formatInlineText = (text: string): React.ReactNode => {
  if (!text) return null;
  
  const parts: React.ReactNode[] = [];
  let key = 0;
  let lastEnd = 0;

  // Process both bold (**text**) and italic (*text*)
  const regex = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastEnd) {
      const beforeText = text.substring(lastEnd, match.index);
      if (beforeText) {
        parts.push(beforeText);
      }
    }

    // Add formatted text
    if (match[0].startsWith('**')) {
      // Bold
      parts.push(
        <strong key={`bold-${key++}`} className="font-semibold text-gray-900">
          {match[2]}
        </strong>
      );
    } else {
      // Italic
      parts.push(
        <em key={`italic-${key++}`} className="italic">
          {match[3]}
        </em>
      );
    }

    lastEnd = match.index + match[0].length;
  }

  // Add remaining text
  if (lastEnd < text.length) {
    parts.push(text.substring(lastEnd));
  }

  return parts.length > 0 ? <>{parts}</> : text;
};

